import React, { useState, useEffect, useCallback, useRef, useMemo } from 'react';
import axios from 'axios';
import {
  Box,
  Typography,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Button,
  TextField,
  Autocomplete,
  Grid,
  Paper,
  IconButton,
  CircularProgress,
  Chip,
  Tooltip,
  Divider,
  InputAdornment,
  Alert,
  alpha
} from '@mui/material';
import {
  Add as AddIcon,
  Delete as DeleteIcon,
  ExpandMore as ExpandMoreIcon,
  ContentCopy as ContentCopyIcon,
  Check as CheckIcon,
  Warning as WarningIcon,
  History,
  KeyboardReturn as KeyboardReturnIcon,
  SwapHoriz as SwapHorizIcon,
  Save as SaveIcon
} from '@mui/icons-material';
import { User, Storage, Material, TableRowData, Pharmacy, BoxCode } from './types';
import { isFormValid, calculateMarkCodesCount, createMarkCodesArray, formatDate, getStatusText } from './utils';
import { generateBoxCodes } from './qrGenerator';
import BoxQRPrintView from './BoxQRPrintView';
import BoxScanningView from './BoxScanningView';
import MarkCodesInput from './MarkCodesInput';
import DocumentLayout, { colors } from './DocumentLayout';

interface DocumentDialogProps {
  open: boolean;
  isEditMode: boolean;
  documentType: number;
  documentNumber?: number;
  documentStatus?: number | null;
  editingDocument?: any;
  toPharmacy: Pharmacy | null;
  pharmacyNum: string;
  tableData: TableRowData[];
  users: User[];
  materials: Material[];
  materialSearchInputs: { [key: string]: string };
  pharmacySearchInput: string;
  filteredPharmacies: User[];
  isSaving: boolean;
  throughRC: boolean | null;
  boxCount: number;
  comment: string;
  isMarkedProduct?: boolean;
  storages: Storage[];
  toStorage: Storage | null;
  storageSearchInput: string;
  filteredStorages: Storage[];
  onClose: () => void;
  onCancel: () => void;
  onDocumentTypeChange: (type: number) => void;
  onPharmacySearchChange: (value: string) => void;
  onPharmacySelect: (user: User | null) => void;
  onStorageSearchChange: (value: string) => void;
  onStorageSelect: (storage: Storage | null) => void;
  onAddTableRow: () => void;
  onRemoveTableRow: (id: string) => void;
  onUpdateTableRow: (id: string, field: keyof TableRowData, value: string) => void;
  onMaterialSearchChange: (rowId: string, value: string) => void;
  onMaterialSelect: (rowId: string, material: Material) => void;
  onSaveDocument: (status: number) => void;
  onSaveAsDraft: () => Promise<string | null>;
  onCompleteWithBoxes: (documentId: string, boxes: BoxCode[]) => Promise<void>;
  onThroughRCChange: (checked: boolean) => void;
  onBoxCountChange: (count: number) => void;
  onCommentChange: (comment: string) => void;
}

const DocumentDialog: React.FC<DocumentDialogProps> = ({
  open,
  isEditMode,
  documentType,
  documentNumber,
  documentStatus,
  editingDocument,
  toPharmacy,
  pharmacyNum,
  tableData,
  users,
  materials,
  materialSearchInputs,
  pharmacySearchInput,
  filteredPharmacies,
  isSaving,
  throughRC,
  boxCount,
  comment,
  isMarkedProduct = false,
  storages,
  toStorage,
  storageSearchInput,
  filteredStorages,
  onClose,
  onCancel,
  onDocumentTypeChange,
  onPharmacySearchChange,
  onPharmacySelect,
  onStorageSearchChange,
  onStorageSelect,
  onAddTableRow,
  onRemoveTableRow,
  onUpdateTableRow,
  onMaterialSearchChange,
  onMaterialSelect,
  onSaveDocument,
  onSaveAsDraft,
  onCompleteWithBoxes,
  onThroughRCChange,
  onBoxCountChange,
  onCommentChange
}) => {
  const [expandedItems, setExpandedItems] = useState<{ [key: string]: boolean }>({});
  const [currentStep, setCurrentStep] = useState<'form' | 'qr-print' | 'scanning'>('form');
  const [boxCodes, setBoxCodes] = useState<BoxCode[]>([]);
  const [validationErrors, setValidationErrors] = useState<string[]>([]);
  const [duplicateCodes, setDuplicateCodes] = useState<Set<string>>(new Set());
  const [savedDocumentId, setSavedDocumentId] = useState<string | null>(null);

  const formValid = isFormValid(documentType, toPharmacy, tableData, toStorage);

  // Режим "Сканирование коробов" - только boxCount можно редактировать
  const isScanningMode = documentStatus === 12;

  // Сброс состояния при открытии/закрытии диалога
  useEffect(() => {
    if (open) {
      setCurrentStep('form');
      setBoxCodes([]);
      setValidationErrors([]);
      setSavedDocumentId(null);
    }
  }, [open]);

  // Детальная валидация формы
  const validateDocumentForm = (): { valid: boolean; errors: string[] } => {
    const errors: string[] = [];

    if (documentType === 5 && !toPharmacy) {
      errors.push('Выберите аптеку получателя');
    }

    if (documentType === 4 && !toStorage) {
      errors.push('Выберите склад получателя');
    }

    if (documentType === 5 && throughRC === null) {
      errors.push('Выберите значение "Через РЦ"');
    }

    if (tableData.length === 0) {
      errors.push('Добавьте хотя бы один товар');
    }

    tableData.forEach((row, index) => {
      if (!row.selectedMaterial) {
        errors.push(`Товар №${index + 1}: не выбран из справочника`);
      }
      if (!row.MaterialCode) {
        errors.push(`Товар №${index + 1}: отсутствует код товара`);
      }
      if (!row.MaterialName) {
        errors.push(`Товар №${index + 1}: отсутствует наименование`);
      }

      const hasQuantity = row.Quantity && parseFloat(row.Quantity) > 0;
      const hasQuantityPcs = row.QuantityPcs && parseFloat(row.QuantityPcs) > 0;
      if (!hasQuantity && !hasQuantityPcs) {
        errors.push(`Товар №${index + 1}: не указано количество (упак или шт)`);
      }

      const stockMax = row.selectedMaterial?.StockCount;
      if (stockMax !== undefined && hasQuantity && parseFloat(row.Quantity) > stockMax) {
        errors.push(`Товар №${index + 1}: количество (${row.Quantity} упак) превышает остаток (${stockMax} упак)`);
      }


      const neededCodesCount = calculateMarkCodesCount(
        row.Quantity,
        row.QuantityPcs,
        row.selectedMaterial?.PartCoeff
      );
      const markCodes = row.markCodes ? (Array.isArray(row.markCodes) ? row.markCodes : JSON.parse(row.markCodes || '[]')) : [];
      const filledCodesCount = markCodes.filter((code: string) => code.length === 31).length;
      const invalidCodes = markCodes.filter((code: string) => code.length > 0 && code.length !== 31);

      // Каждый введённый код должен быть строго 31 символ
      if (invalidCodes.length > 0) {
        errors.push(`Товар №${index + 1}: ${invalidCodes.length} код(ов) имеют неверную длину (должно быть 31 символ)`);
      }

      // Проверка количества - при частичном сканировании не требуем все коды, но каждый введённый должен быть 31 символ
      // Для маркированного возврата все коды строго обязательны
      const isStrictMarking = documentType === 4 && isMarkedProduct;
      if ((isStrictMarking || !row.partialScanning) && filledCodesCount < neededCodesCount) {
        errors.push(
          isStrictMarking
            ? `Товар №${index + 1}: заполнено ${filledCodesCount} из ${neededCodesCount} маркировочных кодов (для маркированного возврата требуются все коды)`
            : `Товар №${index + 1}: заполнено ${filledCodesCount} из ${neededCodesCount} маркировочных кодов`
        );
      }

      // Для документов типа 4 (Возврат/склад) серии обязательны
      if (documentType === 4) {
        const qty = Math.ceil(parseFloat(row.Quantity || '0') || 0);
        const seriesArr: any[] = row.series
          ? (() => { try { return JSON.parse(row.series); } catch { return []; } })()
          : [];
        const filledSeries = seriesArr.filter(s => {
          const name = typeof s === 'string' ? s : (s?.name || '');
          return name && name.trim();
        }).length;
        if (qty > 0 && filledSeries !== qty) {
          if (filledSeries < qty) {
            errors.push(`Товар №${index + 1}: заполнено ${filledSeries}/${qty} серий`);
          } else {
            errors.push(`Товар №${index + 1}: количество в сериях (${filledSeries}) превышает количество товара (${qty})`);
          }
        }
      }
    });

    if (boxCount <= 0) {
      errors.push('Укажите количество коробов (должно быть больше 0)');
    }

    // Проверка уникальности маркировочных кодов в рамках документа
    const allCodes: { code: string; rowIndex: number }[] = [];
    tableData.forEach((row, index) => {
      const codes = row.markCodes ? (Array.isArray(row.markCodes) ? row.markCodes : JSON.parse(row.markCodes || '[]')) : [];
      codes.forEach((code: string) => {
        if (code.length === 31) {
          allCodes.push({ code, rowIndex: index });
        }
      });
    });

    const codeCountMap = new Map<string, number[]>();
    allCodes.forEach(({ code, rowIndex }) => {
      if (!codeCountMap.has(code)) {
        codeCountMap.set(code, []);
      }
      codeCountMap.get(code)!.push(rowIndex);
    });

    const dupes = new Set<string>();
    codeCountMap.forEach((rowIndices, code) => {
      if (rowIndices.length > 1) {
        dupes.add(code);
        const uniqueRows = Array.from(new Set(rowIndices)).map(i => i + 1).join(', ');
        errors.push(`Дублирующийся код маркировки в товарах №${uniqueRows}: ...${code.slice(-8)}`);
      }
    });
    setDuplicateCodes(dupes);

    // Автоматически развернуть товары с дублирующимися кодами
    if (dupes.size > 0) {
      const rowsWithDupes = new Set<string>();
      tableData.forEach((row) => {
        const codes = row.markCodes ? (Array.isArray(row.markCodes) ? row.markCodes : JSON.parse(row.markCodes || '[]')) : [];
        if (codes.some((c: string) => dupes.has(c))) {
          rowsWithDupes.add(row.id);
        }
      });
      setExpandedItems(prev => {
        const updated = { ...prev };
        rowsWithDupes.forEach(id => { updated[id] = true; });
        return updated;
      });
    }

    return {
      valid: errors.length === 0,
      errors
    };
  };

  // Полная валидация формы для сохранения черновика (те же правила, что и для отправки в работу)
  const validateDraftForm = (): { valid: boolean; errors: string[] } => {
    return validateDocumentForm();
  };

  // Обработчик сохранения черновика
  const handleSaveDraftClick = useCallback(() => {
    const validation = validateDraftForm();
    if (!validation.valid) {
      setValidationErrors(validation.errors);
      return;
    }
    setValidationErrors([]);
    onSaveDocument(9);
  }, [validateDraftForm, onSaveDocument]);

  // Переход к печати QR-кодов (сначала сохраняем как черновик)
  const handleProceedToQR = async () => {
    const validation = validateDocumentForm();

    if (!validation.valid) {
      setValidationErrors(validation.errors);
      return;
    }

    try {
      // Всегда сохраняем актуальные данные формы и таблицы в БД (onSaveAsDraft)
      // как для новых документов, так и при редактировании черновика
      const targetDocId = await onSaveAsDraft();
      if (!targetDocId) {
        setValidationErrors(['Не удалось сохранить документ. Попробуйте снова.']);
        return;
      }

      setSavedDocumentId(targetDocId);
      const codes = generateBoxCodes(targetDocId, boxCount);
      setBoxCodes(codes);
      setValidationErrors([]);
      setCurrentStep('qr-print');
    } catch (error) {
      console.error('Ошибка при сохранении документа перед QR:', error);
      setValidationErrors(['Ошибка при сохранении документа. Попробуйте снова.']);
    }
  };

  const handleProceedToScanning = () => {
    setCurrentStep('scanning');
  };

  const handleBackToPrint = () => {
    setCurrentStep('qr-print');
  };

  const handleBackToForm = () => {
    setCurrentStep('form');
    setBoxCodes([]);
    setValidationErrors([]);
  };

  const handleBoxScanned = (boxNum: number, code: string) => {
    setBoxCodes(prev => prev.map(box =>
      box.box_num === boxNum
        ? { ...box, scanned: true, scannedAt: new Date() }
        : box
    ));
  };

  const handleCompleteScanningAndSave = async () => {
    try {
      const docId = savedDocumentId || documentNumber?.toString();
      if (!docId) {
        setValidationErrors(['Ошибка: не найден ID документа']);
        return;
      }
      await onCompleteWithBoxes(docId, boxCodes);
    } catch (error) {
      console.error('Ошибка при завершении сканирования:', error);
      setValidationErrors(['Ошибка при сохранении коробов. Попробуйте снова.']);
    }
  };

  // Горячие клавиши
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (currentStep !== 'form') return;

      if ((e.ctrlKey || e.metaKey) && e.key === 'Enter' && formValid && !isSaving) {
        e.preventDefault();
        handleProceedToQR();
      }
      if ((e.ctrlKey || e.metaKey) && e.key === 's' && !isSaving) {
        e.preventDefault();
        handleSaveDraftClick();
      }
      if (e.key === 'Escape' && !isSaving) {
        onClose();
      }
    };

    if (open) {
      window.addEventListener('keydown', handleKeyDown);
      return () => window.removeEventListener('keydown', handleKeyDown);
    }
  }, [open, formValid, isSaving, handleSaveDraftClick, onClose, currentStep]);

  // Автоматически разворачивать новые товары
  useEffect(() => {
    if (tableData.length > 0) {
      const lastItem = tableData[tableData.length - 1];
      if (!expandedItems[lastItem.id] && !lastItem.MaterialName) {
        setExpandedItems(prev => ({ ...prev, [lastItem.id]: true }));
      }
    }
  }, [tableData.length]);

  const handleExpandToggle = (rowId: string) => {
    setExpandedItems(prev => ({ ...prev, [rowId]: !prev[rowId] }));
  };

  // Клонирование товара
  const handleCloneRow = (row: TableRowData) => {
    const clonedRow: TableRowData = {
      id: Date.now().toString() + Math.random(),
      MaterialZid: row.MaterialZid,
      MaterialCode: row.MaterialCode,
      MaterialName: row.MaterialName,
      SerialNumber: row.SerialNumber,
      SerialDate: row.SerialDate,
      Quantity: row.Quantity,
      Quantity1C: row.Quantity1C || '',
      Barcode: row.Barcode,
      Comment: row.Comment || '',
      selectedMaterial: row.selectedMaterial,
      markCodes: [],
      series: row.series || '[]',
      partialScanning: row.partialScanning
    };

    const index = tableData.findIndex(r => r.id === row.id);
    const newTableData = [
      ...tableData.slice(0, index + 1),
      clonedRow,
      ...tableData.slice(index + 1)
    ];

    onAddTableRow();
    setTimeout(() => {
      setExpandedItems(prev => ({ ...prev, [clonedRow.id]: true }));
    }, 100);
  };

  // Проверка заполненности строки
  const isRowComplete = (row: TableRowData): boolean => {
    const hasQuantity = row.Quantity && parseFloat(row.Quantity) > 0;
    const hasQuantityPcs = row.QuantityPcs && parseFloat(row.QuantityPcs) > 0;
    return !!(
      row.selectedMaterial &&
      row.MaterialCode &&
      row.MaterialName &&
      (hasQuantity || hasQuantityPcs)
    );
  };

  const getDocumentTypeName = (type: number): string => {
    switch (type) {
      case 4: return 'Возврат';
      case 5: return 'Перемещение между аптеками';
      default: return 'Документ';
    }
  };

  // Подсчет заполненных товаров
  const completedCount = tableData.filter(isRowComplete).length;
  const totalCount = tableData.length;

  // === Read-only sidebar для шагов QR/сканирования (дизайн как в просмотре) ===
  const readOnlySidebar = (<>
    <Box sx={{ p: 3 }}>
      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
        {/* Отправитель + Получатель */}
        <Grid container spacing={2}>
          <Grid item xs={6}>
            <Typography variant="caption" sx={{ color: 'text.secondary', fontWeight: 500, display: 'block', mb: 0.5 }}>Отправитель</Typography>
            <Typography variant="body2" sx={{ fontWeight: 600, color: colors.dark, lineHeight: 1.2 }}>Аптека №{pharmacyNum}</Typography>
          </Grid>
          <Grid item xs={6}>
            <Typography variant="caption" sx={{ color: 'text.secondary', fontWeight: 500, display: 'block', mb: 0.5 }}>Получатель</Typography>
            <Typography variant="body2" sx={{ fontWeight: 600, color: colors.dark, lineHeight: 1.2 }}>
              {documentType === 5 && toPharmacy ? `Аптека №${toPharmacy.pharmacyNum}` : (toStorage ? toStorage.username : localStorage.getItem('belongsStorage'))}
            </Typography>
          </Grid>
        </Grid>

        <Divider sx={{ borderStyle: 'dashed' }} />

        {/* Тип + Через РЦ / Маркировка */}
        <Grid container spacing={2}>
          <Grid item xs={6}>
            <Typography variant="caption" sx={{ color: 'text.secondary', fontWeight: 500, display: 'block', mb: 0.5 }}>Тип</Typography>
            <Typography variant="body2" sx={{ fontWeight: 600, color: colors.dark }}>{getDocumentTypeName(documentType)}</Typography>
          </Grid>
          {documentType === 5 && (
            <Grid item xs={6}>
              <Typography variant="caption" sx={{ color: 'text.secondary', fontWeight: 500, display: 'block', mb: 0.5 }}>Через РЦ</Typography>
              <Typography variant="body2" sx={{ fontWeight: 600, color: colors.dark }}>{throughRC ? 'Да' : 'Нет (Прямое)'}</Typography>
            </Grid>
          )}
          {documentType === 4 && (
            <Grid item xs={6}>
              <Typography variant="caption" sx={{ color: 'text.secondary', fontWeight: 500, display: 'block', mb: 0.5 }}>Маркировка</Typography>
              <Typography variant="body2" sx={{ fontWeight: 600, color: colors.dark }}>{isMarkedProduct ? 'Маркированный' : 'Без маркировки'}</Typography>
            </Grid>
          )}
        </Grid>

        <Divider sx={{ borderStyle: 'dashed' }} />

        {/* Коробов + Товаров */}
        <Grid container spacing={2}>
          <Grid item xs={6}>
            <Typography variant="caption" sx={{ color: 'text.secondary', fontWeight: 500, display: 'block', mb: 0.5 }}>Количество коробов</Typography>
            <Typography variant="body2" sx={{ fontWeight: 600, color: colors.dark }}>{boxCount}</Typography>
          </Grid>
          <Grid item xs={6}>
            <Typography variant="caption" sx={{ color: 'text.secondary', fontWeight: 500, display: 'block', mb: 0.5 }}>Товаров</Typography>
            <Typography variant="body2" sx={{ fontWeight: 600, color: colors.dark }}>{totalCount}</Typography>
          </Grid>
        </Grid>

      </Box>

      {/* Оформленный комментарий */}
      {comment && (
        <Paper variant="outlined" sx={{ p: 2, mt: 2, bgcolor: alpha(colors.warning, 0.05), borderColor: alpha(colors.warning, 0.2) }}>
          <Typography variant="caption" gutterBottom sx={{ color: colors.warning, fontWeight: 700, display: 'flex', alignItems: 'center', gap: 1 }}>
            <WarningIcon fontSize="inherit" /> КОММЕНТАРИЙ
          </Typography>
          <Typography variant="body2" sx={{ color: colors.dark }}>{comment}</Typography>
        </Paper>
      )}
    </Box>

    <Divider />

    {/* Даты создания/обновления */}
    {
      editingDocument && (
        <Box sx={{ p: 3 }}>
          <Typography variant="caption" sx={{ color: 'text.secondary', fontWeight: 500, display: 'block', mb: 0.5 }}>Создан / Обновлен</Typography>
          <Typography variant="body2" sx={{ fontWeight: 500 }}>
            {formatDate(editingDocument.createdAtISO || editingDocument.createdAt)}
          </Typography>
          {editingDocument.updateDate && (
            <Typography variant="caption" color="text.secondary">
              {formatDate(editingDocument.updateDateISO || editingDocument.updateDate)}
            </Typography>
          )}
        </Box>
      )
    }

    <Divider />

    {/* История статусов */}
    {
      editingDocument?.statusHistory && editingDocument.statusHistory.length > 0 && (
        <Box sx={{ p: 3, flexGrow: 1 }}>
          <Typography variant="subtitle2" sx={{ mb: 2, fontWeight: 700, color: colors.dark, display: 'flex', alignItems: 'center', gap: 1 }}>
            <History fontSize="small" color="disabled" />
            История статусов
          </Typography>
          <Box sx={{ position: 'relative', pl: 1 }}>
            <Box sx={{ position: 'absolute', top: 4, bottom: 4, left: 12, width: 2, bgcolor: colors.light }} />
            {editingDocument.statusHistory.map((h: any, idx: number) => (
              <Box key={idx} sx={{ display: 'flex', gap: 2, mb: 2.5, position: 'relative' }}>
                <Box sx={{
                  width: 10, height: 10, borderRadius: '50%', mt: 0.5, flexShrink: 0,
                  bgcolor: idx === 0 ? colors.primary : colors.border,
                  boxShadow: idx === 0 ? `0 0 0 2px ${alpha(colors.primary, 0.3)}` : 'none',
                  zIndex: 1
                }} />
                <Box>
                  <Typography variant="body2" sx={{ fontWeight: 600, color: colors.dark, lineHeight: 1.2 }}>
                    {h.statusName}
                  </Typography>
                  <Typography variant="caption" sx={{ color: 'text.secondary', display: 'block', mt: 0.5 }}>
                    {h.recordDate} • {h.username}
                  </Typography>
                  {h.description && (
                    <Typography variant="caption" sx={{ display: 'block', mt: 0.5, color: 'text.secondary', fontStyle: 'italic', bgcolor: colors.light, p: 0.5, borderRadius: 1 }}>
                      {h.description}
                    </Typography>
                  )}
                </Box>
              </Box>
            ))}
          </Box>
        </Box>
      )
    }
  </>);

  // === Шаги QR и сканирования в DocumentLayout ===
  if (currentStep === 'qr-print') {
    return (
      <DocumentLayout
        open={open}
        onClose={() => { }}
        title="ПЕЧАТЬ QR-КОДОВ"
        subtitle={`#${savedDocumentId || documentNumber || ''}`}
        sidebar={readOnlySidebar}
        loading={false}
        disableEscapeKeyDown
      >
        <BoxQRPrintView
          boxes={boxCodes}
          documentNumber={(savedDocumentId || documentNumber?.toString()) || 'ERROR'}
          pharmacyNum={pharmacyNum}
          toPharmacyNum={toPharmacy?.pharmacyNum}
          documentType={documentType}
          throughRC={throughRC}
          routeText={(() => {
            const from = `Аптека №${pharmacyNum}`;
            if (documentType === 5 && throughRC && toPharmacy) {
              const senderDC = localStorage.getItem('belongsStorage') || '';
              const receiverDC = toPharmacy.belongsStorage || '';
              const to = `Аптека №${toPharmacy.pharmacyNum}`;
              if (senderDC && receiverDC && senderDC !== receiverDC) {
                return `${from} → ${senderDC} → ${receiverDC} → ${to}`;
              } else if (senderDC) {
                return `${from} → ${senderDC} → ${to}`;
              }
              return `${from} → ${to}`;
            } else if (documentType === 5 && toPharmacy) {
              return `${from} → Аптека №${toPharmacy.pharmacyNum}`;
            } else if (documentType === 4 && toStorage) {
              return `${from} → ${toStorage.username}`;
            }
            return '';
          })()}
          onNext={handleProceedToScanning}
          onBack={handleBackToForm}
        />
      </DocumentLayout>
    );
  }

  if (currentStep === 'scanning') {
    return (
      <DocumentLayout
        open={open}
        onClose={() => { }}
        title="СКАНИРОВАНИЕ КОРОБОВ"
        subtitle={`#${savedDocumentId || documentNumber || ''}`}
        sidebar={readOnlySidebar}
        loading={false}
        disableEscapeKeyDown
      >
        <BoxScanningView
          boxes={boxCodes}
          onBoxScanned={handleBoxScanned}
          onComplete={handleCompleteScanningAndSave}
          onBack={handleBackToPrint}
        />
      </DocumentLayout>
    );
  }



  // === Sidebar - form controls ===
  const sidebarContent = (
    <>
      <Box sx={{ p: 3 }}>
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2.5 }}>

          {/* Отправитель + Получатель в одной строке (как в форме просмотра) */}
          <Grid container spacing={2} alignItems="flex-end">
            <Grid item xs={6}>
              <Typography variant="caption" sx={{ color: 'text.secondary', fontWeight: 500, display: 'block', mb: 0.5 }}>Отправитель</Typography>
              <Typography variant="body2" sx={{ fontWeight: 600, color: colors.dark, lineHeight: 1.2 }}>Аптека №{pharmacyNum}</Typography>
            </Grid>
            <Grid item xs={6}>
              {documentType === 5 ? (
                <Autocomplete
                  size="small"
                  options={filteredPharmacies}
                  getOptionLabel={(option) => `№${option.pharmacyNum}`}
                  value={toPharmacy ? users.find(u => u.pharmacyNum === toPharmacy.pharmacyNum) || null : null}
                  inputValue={pharmacySearchInput}
                  onInputChange={(event, newInputValue) => {
                    onPharmacySearchChange(newInputValue);
                  }}
                  onChange={(event, newValue) => {
                    onPharmacySelect(newValue);
                  }}
                  disabled={isScanningMode}
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      label="Получатель"
                      placeholder="Аптека..."
                      required
                      error={!toPharmacy}
                      size="small"
                      sx={{ '& .MuiInputBase-root': { fontSize: '0.85rem' } }}
                    />
                  )}
                  renderOption={(props, option) => (
                    <Box component="li" {...props}>
                      <Box>
                        <Typography variant="body2" component="div" fontWeight="medium">
                          Аптека №{option.pharmacyNum}
                        </Typography>
                        {option.fullName && (
                          <Typography variant="caption" color="text.secondary">
                            {option.fullName}
                          </Typography>
                        )}
                      </Box>
                    </Box>
                  )}
                  noOptionsText="Не найдено"
                />
              ) : (
                <Autocomplete
                  size="small"
                  options={filteredStorages.length > 0 ? filteredStorages : storages}
                  getOptionLabel={(option) => option.username}
                  isOptionEqualToValue={(option, val) => {
                    if (!option || !val) return false;
                    if (option.id && val.id && option.id === val.id) return true;
                    if (option.guid && val.guid && option.guid.toLowerCase() === val.guid.toLowerCase()) return true;
                    if (option.username && val.username && option.username.toLowerCase() === val.username.toLowerCase()) return true;
                    return false;
                  }}
                  value={
                    toStorage
                      ? (storages.find(s =>
                        (toStorage.guid && s.guid && s.guid.toLowerCase() === toStorage.guid.toLowerCase()) ||
                        (toStorage.id && s.id === toStorage.id) ||
                        (toStorage.username && s.username.toLowerCase() === toStorage.username.toLowerCase())
                      ) || toStorage)
                      : null
                  }
                  inputValue={storageSearchInput}
                  onInputChange={(event, newInputValue, reason) => {
                    if (reason === 'input') {
                      onStorageSearchChange(newInputValue);
                    } else if (reason === 'clear') {
                      onStorageSearchChange('');
                    } else if (reason === 'reset' && !newInputValue && !toStorage) {
                      onStorageSearchChange('');
                    }
                  }}
                  onChange={(event, newValue) => {
                    onStorageSelect(newValue);
                  }}
                  disabled={isScanningMode}
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      label="Получатель (склад)"
                      placeholder="Склад..."
                      required
                      error={!toStorage}
                      size="small"
                      sx={{ '& .MuiInputBase-root': { fontSize: '0.85rem' } }}
                    />
                  )}
                  renderOption={(props, option) => (
                    <Box component="li" {...props} key={option.id || option.guid || option.username}>
                      <Box sx={{ display: 'flex', flexDirection: 'column' }}>
                        <Typography variant="body2" component="div" fontWeight="medium">
                          {option.username}
                        </Typography>
                        {option.cityId && (
                          <Typography variant="caption" color="text.secondary">
                            Склад {option.cityId}
                          </Typography>
                        )}
                      </Box>
                    </Box>
                  )}
                  noOptionsText="Не найдено"
                />
              )}
            </Grid>
          </Grid>

          <Divider sx={{ borderStyle: 'dashed' }} />

          {/* Тип документа + Через РЦ / Маркировка */}
          <Grid container spacing={2}>
            <Grid item xs={6}>
              <FormControl fullWidth size="small">
                <InputLabel>Тип документа</InputLabel>
                <Select
                  value={documentType}
                  label="Тип документа"
                  onChange={(e) => onDocumentTypeChange(Number(e.target.value))}
                  required
                  disabled={isEditMode}
                >
                  <MenuItem value={4}>Перемещение (склад)</MenuItem>
                  <MenuItem value={5}>Перемещение (аптеки)</MenuItem>
                </Select>
              </FormControl>
            </Grid>
            {documentType === 5 && (
              <Grid item xs={6}>
                <FormControl fullWidth size="small" error={throughRC === null}>
                  <InputLabel>Через РЦ *</InputLabel>
                  <Select
                    value={throughRC === null ? '' : (throughRC ? 'yes' : 'no')}
                    label="Через РЦ *"
                    onChange={(e) => onThroughRCChange(e.target.value === 'yes')}
                    required
                  >
                    <MenuItem value="no">Нет (Прямое)</MenuItem>
                    <MenuItem value="yes">Да (Через РЦ)</MenuItem>
                  </Select>
                </FormControl>
              </Grid>
            )}
            {documentType === 4 && (
              <Grid item xs={6}>
                <TextField
                  fullWidth
                  size="small"
                  label="Маркировка"
                  value={isMarkedProduct ? 'Маркированный (строго)' : 'Без маркировки (частично)'}
                  InputProps={{
                    readOnly: true,
                  }}
                  disabled
                  helperText="Параметр задается при создании документа"
                  sx={{ '& .MuiInputBase-root': { fontSize: '0.85rem' } }}
                />
              </Grid>
            )}
          </Grid>

          <Divider sx={{ borderStyle: 'dashed' }} />

          {/* Количество коробов */}
          <Box>
            <Typography variant="caption" sx={{ color: 'text.secondary', fontWeight: 500, display: 'block', mb: 0.5 }}>Количество коробов *</Typography>
            <TextField
              fullWidth
              type="number"
              variant="outlined"
              size="small"
              value={boxCount === 0 ? '' : boxCount}
              onChange={(e) => onBoxCountChange(Number(e.target.value) || 0)}
              inputProps={{ min: 0, step: 1 }}
              placeholder="0"
              error={boxCount <= 0}
              helperText={boxCount <= 0 ? 'Укажите количество коробов' : ''}
            />
          </Box>

          <Divider sx={{ borderStyle: 'dashed' }} />

          {/* Комментарий */}
          <TextField
            fullWidth
            multiline
            rows={3}
            size="small"
            label="Комментарий"
            value={comment}
            onChange={(e) => onCommentChange(e.target.value)}
            placeholder="Необязательно"
            variant="outlined"
            disabled={isScanningMode}
            sx={{
              '& .MuiOutlinedInput-root': {
                backgroundColor: '#fafafa',
                fontSize: '0.875rem'
              },
              mb: 2
            }}
          />
        </Box>

        {/* Статистика - сумма и количество */}
        {(() => {
          const filledRows = tableData.filter(r => r.selectedMaterial && r.Quantity && parseFloat(r.Quantity) > 0);
          if (filledRows.length === 0) return null;

          const totalSum = filledRows.reduce((acc, r) => {
            const price = r.selectedMaterial?.StockPrice ?? 0;
            const qty = parseFloat(r.Quantity) || 0;
            return acc + price * qty;
          }, 0);

          const totalQuantity = filledRows.reduce((acc, r) => {
            return acc + (parseFloat(r.Quantity) || 0);
          }, 0);

          const hasAnyPrice = filledRows.some(r => (r.selectedMaterial?.StockPrice ?? 0) > 0);

          return (
            <>
              <Divider sx={{ borderStyle: 'dashed' }} />
              <Box sx={{
                mt: 2,
                mx: 0,
                px: 2,
                py: 1.5,
                bgcolor: alpha(colors.primary, 0.04),
                borderTop: `1px solid ${alpha(colors.primary, 0.1)}`,
                borderBottom: `1px solid ${alpha(colors.primary, 0.1)}`,
              }}>
                <Typography variant="caption" sx={{ color: 'text.secondary', fontWeight: 700, textTransform: 'uppercase', letterSpacing: 0.5, display: 'block', mb: 1 }}>
                  Итого
                </Typography>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 0.5 }}>
                  <Typography variant="body2" color="text.secondary">Позиций:</Typography>
                  <Typography variant="body2">{filledRows.length}</Typography>
                </Box>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 0.5 }}>
                  <Typography variant="body2" color="text.secondary">Количество:</Typography>
                  <Typography variant="body2">{totalQuantity} упак</Typography>
                </Box>
                {hasAnyPrice && (
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                    <Typography variant="body2" color="text.secondary">Сумма:</Typography>
                    <Typography variant="body2">
                      {totalSum.toLocaleString('ru-RU', { minimumFractionDigits: 0, maximumFractionDigits: 2 })} ₸
                    </Typography>
                  </Box>
                )}
              </Box>
            </>
          );
        })()}
      </Box>
    </>
  );

  return (
    <DocumentLayout
      open={open}
      onClose={onClose}
      title={isEditMode ? 'РЕДАКТИРОВАНИЕ' : 'НОВЫЙ ДОКУМЕНТ'}
      subtitle={documentNumber ? `#${documentNumber}` : undefined}
      sidebar={sidebarContent}
      loading={false}
      disableEscapeKeyDown={isSaving}
    >
      {/* Заголовок товаров */}
      <Box sx={{ p: 2, display: 'flex', alignItems: 'center', justifyContent: 'space-between', bgcolor: 'white', borderBottom: `1px solid ${colors.border}` }}>
        <Typography variant="subtitle1" sx={{ fontWeight: 700, color: colors.dark }}>
          Товары
          <Chip label={tableData.length} size="small" sx={{ ml: 1, bgcolor: 'grey.100', fontWeight: 'bold' }} />
        </Typography>

      </Box>

      {/* Список товаров (Accordion) */}
      <Box sx={{ p: 2, flexGrow: 1, overflow: 'auto', position: 'relative' }}>
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1, mb: 2 }}>
          {tableData.map((row, index) => (
            <CompactMaterialRow
              key={row.id}
              row={row}
              index={index}
              materials={materials}
              materialSearchInputs={materialSearchInputs}
              tableDataLength={tableData.length}
              isExpanded={expandedItems[row.id] || false}
              isComplete={isRowComplete(row)}
              isDisabled={isScanningMode}
              duplicateCodes={duplicateCodes}
              documentType={documentType}
              isMarkedProduct={isMarkedProduct}
              onExpandToggle={() => handleExpandToggle(row.id)}
              onCloneRow={() => handleCloneRow(row)}
              onRemoveTableRow={onRemoveTableRow}
              onUpdateTableRow={onUpdateTableRow}
              onMaterialSearchChange={onMaterialSearchChange}
              onMaterialSelect={onMaterialSelect}
            />
          ))}
        </Box>
        <Button
          variant="contained"
          size="small"
          startIcon={<AddIcon />}
          onClick={onAddTableRow}
          disabled={isScanningMode}
          sx={{ px: 2, width: '100%' }}
        >
          Добавить товар
        </Button>
        {/* Ошибки валидации */}
        {validationErrors.length > 0 && (
          <Box sx={{ mt: 2, position: 'sticky', bottom: 0, left: 0, right: 0, zIndex: 9999 }}>
            <Alert severity="error" onClose={() => setValidationErrors([])}>
              <Typography variant="subtitle2" fontWeight="bold" gutterBottom>
                Обнаружены ошибки валидации:
              </Typography>
              <Box component="ul" sx={{ pl: 2, m: 0 }}>
                {validationErrors.map((error, index) => (
                  <li key={index}>
                    <Typography variant="body2">{error}</Typography>
                  </li>
                ))}
              </Box>
            </Alert>
            <Box sx={{ mt: 1, borderTop: `1px solid ${colors.border}`, backgroundColor: colors.warning, p: 2, borderRadius: 1, color: 'white', minWidth: '100%' }}>
              <Typography variant="body2">
                {documentType === 4 && isMarkedProduct
                  ? 'Разверните товар и отсканируйте недостающие коды маркировки (для маркированного возврата все коды обязательны). Если присутствуют дублирующиеся коды - устраните их.'
                  : 'Разверните товар и отсканируйте недостающие коды, либо установите галочку "Не требовать все коды". Если присутствуют дублирующиеся коды - устраните их.'}
              </Typography>
            </Box>
          </Box>
        )}
      </Box>


      {/* Action buttons - внутри правой панели */}
      <Box sx={{
        p: 2,
        display: 'flex',
        justifyContent: 'flex-end',
        alignItems: 'center',
        gap: 1.5,
        borderTop: `1px solid ${colors.border}`,
        bgcolor: 'white',
        flexShrink: 0
      }}>
        <Button
          onClick={onCancel}
          disabled={isSaving}
          color="inherit"
          sx={{ color: 'text.secondary' }}
        >
          {isScanningMode ? 'Закрыть' : 'Отмена'}
        </Button>

        {/* Кнопка "Сохранить черновик" (мягкая валидация: достаточно получателя и товара с количеством) */}
        {!isScanningMode && (
          <Button
            onClick={handleSaveDraftClick}
            variant="outlined"
            disabled={
              isSaving ||
              (documentType === 5 ? !toPharmacy : !toStorage) ||
              tableData.length === 0 ||
              tableData.every(row => !row.selectedMaterial && !row.MaterialCode)
            }
            color="primary"
            startIcon={isSaving ? <CircularProgress size={18} color="inherit" /> : <SaveIcon />}
            sx={{ px: 2.5, fontWeight: 600 }}
          >
            Сохранить черновик
          </Button>
        )}

        <span>
          <Button
            onClick={handleProceedToQR}
            variant="contained"
            disabled={
              isScanningMode
                ? (isSaving || boxCount <= 0)
                : (!formValid || isSaving || boxCount <= 0 || tableData.some(row => !((parseFloat(row.Quantity) > 0) || (parseFloat(row.QuantityPcs || '0') > 0))))
            }
            color="primary"
            startIcon={isSaving ? <CircularProgress size={20} color="inherit" /> : <CheckIcon />}
            sx={{ px: 3, fontWeight: 'bold', boxShadow: 2 }}
          >
            {isSaving ? 'Отправка...' : 'Отправить в работу'}
          </Button>
        </span>
      </Box>
    </DocumentLayout>
  );
};

// Компактный компонент строки товара с Accordion
interface CompactMaterialRowProps {
  row: TableRowData;
  index: number;
  materials: Material[];
  materialSearchInputs: { [key: string]: string };
  tableDataLength: number;
  isExpanded: boolean;
  isComplete: boolean;
  isDisabled?: boolean;
  duplicateCodes?: Set<string>;
  documentType: number;
  isMarkedProduct?: boolean;
  onExpandToggle: () => void;
  onCloneRow: () => void;
  onRemoveTableRow: (id: string) => void;
  onUpdateTableRow: (id: string, field: keyof TableRowData, value: string) => void;
  onMaterialSearchChange: (rowId: string, value: string) => void;
  onMaterialSelect: (rowId: string, material: Material) => void;
}

const CompactMaterialRow: React.FC<CompactMaterialRowProps> = ({
  row,
  index,
  materials,
  materialSearchInputs,
  tableDataLength,
  isExpanded,
  isComplete,
  isDisabled = false,
  duplicateCodes,
  documentType,
  isMarkedProduct = false,
  onExpandToggle,
  onCloneRow,
  onRemoveTableRow,
  onUpdateTableRow,
  onMaterialSearchChange,
  onMaterialSelect
}) => {
  // LOCAL input value - не вызывает API при каждом нажатии клавиши
  const [localInputValue, setLocalInputValue] = useState(materialSearchInputs[row.id] || '');
  // Флаг: был ли уже выполнен поиск (чтобы показать подсказку Enter)
  const [searchTriggered, setSearchTriggered] = useState(false);

  // Синхронизируем localInputValue если внешний materialSearchInputs изменился
  // (например, при выборе материала имя заполняется снаружи)
  useEffect(() => {
    const external = materialSearchInputs[row.id] || '';
    setLocalInputValue(external);
  }, [materialSearchInputs[row.id]]);

  const handleInputChange = (_event: React.SyntheticEvent, newValue: string, reason: string) => {
    // При выборе из списка - обновляем и снаружи сразу
    if (reason === 'reset' || reason === 'clear') {
      setLocalInputValue(newValue);
      setSearchTriggered(false);
      onMaterialSearchChange(row.id, newValue);
      return;
    }
    // При наборе - только локально, НЕ вызываем onMaterialSearchChange (не дёргаем API)
    setLocalInputValue(newValue);
    setSearchTriggered(false);
  };

  const handleKeyDown = (event: React.KeyboardEvent<HTMLInputElement>) => {
    if (event.key === 'Enter') {
      // Если уже есть выделенный вариант в списке - пусть MUI обработает выбор
      // Мы только запускаем поиск, если список пуст или нет выделения
      event.stopPropagation(); // не даём всплывать (чтобы не закрыть диалог)
      if (localInputValue.trim()) {
        setSearchTriggered(true);
        onMaterialSearchChange(row.id, localInputValue.trim());
      }
    }
  };

  return (
    <Paper
      elevation={0}
      sx={{
        border: '1px solid',
        borderColor: isComplete ? 'success.main' : 'divider',
        borderLeftWidth: 4,
        borderLeftColor: isComplete ? 'success.main' : (row.MaterialName ? 'primary.main' : 'grey.300'),
        borderRadius: 2,
        overflow: 'hidden',
        transition: 'all 0.2s',
        mb: 1,
        '&:hover': {
          boxShadow: 2,
          transform: 'translateY(-1px)'
        }
      }}
    >
      {/* Компактная шапка */}
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          p: 2,
          bgcolor: 'white',
          cursor: 'pointer',
        }}
        onClick={onExpandToggle}
      >
        <IconButton
          size="small"
          sx={{
            mr: 2,
            transform: isExpanded ? 'rotate(180deg)' : 'rotate(0deg)',
            transition: 'transform 0.2s',
            color: 'text.secondary'
          }}
        >
          <ExpandMoreIcon />
        </IconButton>


        <Typography variant="body2" fontWeight="bold" sx={{ minWidth: 30 }}>
          #{index + 1}
        </Typography>

        <Box sx={{ flexGrow: 1, ml: 2 }}>
          {row.MaterialName ? (
            <Box>
              <Typography variant="body2" fontWeight="medium">
                {row.MaterialName}
              </Typography>
              <Typography variant="caption" color="text.secondary">
                {(row.Quantity && parseFloat(row.Quantity) > 0) || (row.QuantityPcs && parseFloat(row.QuantityPcs) > 0) ? (
                  <>
                    Кол-во:
                    {row.Quantity && parseFloat(row.Quantity) > 0 && ` ${row.Quantity} упак`}
                    {row.Quantity && parseFloat(row.Quantity) > 0 && row.QuantityPcs && parseFloat(row.QuantityPcs) > 0 && ','}
                    {row.QuantityPcs && parseFloat(row.QuantityPcs) > 0 && ` ${row.QuantityPcs} шт`}
                  </>
                ) : null}
              </Typography>
            </Box>
          ) : (
            <Typography variant="body2" color="text.secondary" fontStyle="italic">
              Товар не выбран
            </Typography>
          )}
        </Box>

        {isComplete && (
          <Chip
            icon={<CheckIcon />}
            label="Заполнено"
            size="small"
            color="success"
            sx={{ mr: 1 }}
          />
        )}

        {/* Кнопки действий */}
        <Box sx={{ display: 'flex', gap: 0.5 }} onClick={(e) => e.stopPropagation()}>
          <Tooltip title="Клонировать товар">
            <span>
              <IconButton
                size="small"
                onClick={onCloneRow}
                color="primary"
                disabled={isDisabled}
              >
                <ContentCopyIcon fontSize="small" />
              </IconButton>
            </span>
          </Tooltip>
          <Tooltip title="Удалить товар">
            <span>
              <IconButton
                size="small"
                onClick={() => onRemoveTableRow(row.id)}
                disabled={tableDataLength === 1 || isDisabled}
                color="error"
              >
                <DeleteIcon fontSize="small" />
              </IconButton>
            </span>
          </Tooltip>
        </Box>
      </Box>

      {/* Развернутое содержимое */}
      {
        isExpanded && (
          <Box sx={{ p: 2, borderTop: '1px solid #e0e0e0' }}>
            <Grid container spacing={2}>
              {/* Наименование товара - поиск только по Enter */}
              <Grid item xs={12}>
                <Autocomplete
                  size="small"
                  options={materials}
                  getOptionLabel={(option) => option.ProductName}
                  // Отключаем встроенную фильтрацию - фильтрация на сервере
                  filterOptions={(options) => options}
                  value={row.selectedMaterial || null}
                  // Используем локальный state для input
                  inputValue={localInputValue}
                  onInputChange={handleInputChange}
                  onChange={(event, newValue) => {
                    if (newValue) {
                      onMaterialSelect(row.id, newValue);
                    }
                  }}
                  disabled={isDisabled}
                  // Открываем список только если уже был поиск И есть результаты
                  open={searchTriggered && materials.length > 0 && localInputValue.length > 0}
                  onClose={() => setSearchTriggered(false)}
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      label="Наименование товара"
                      placeholder="Введите название, код или штрихкод и нажмите Enter..."
                      required
                      error={!row.MaterialName}
                      helperText={!row.MaterialName ? 'Обязательное поле' : ''}
                      onKeyDown={handleKeyDown}
                      InputProps={{
                        ...params.InputProps,
                        endAdornment: (
                          <>
                            {params.InputProps.endAdornment}
                            {row.MaterialName ? (
                              <InputAdornment position="end">
                                <CheckIcon color="success" fontSize="small" />
                              </InputAdornment>
                            ) : (
                              <InputAdornment position="end">
                                <Chip
                                  label="Enter"
                                  size="small"
                                  icon={<KeyboardReturnIcon style={{ fontSize: 13 }} />}
                                  sx={{
                                    height: 20,
                                    fontSize: '0.7rem',
                                    fontWeight: 600,
                                    bgcolor: 'grey.100',
                                    color: 'text.secondary',
                                    border: '1px solid',
                                    borderColor: 'grey.300',
                                    cursor: 'default',
                                    '& .MuiChip-label': { px: 0.75 },
                                    '& .MuiChip-icon': { ml: 0.5 }
                                  }}
                                />
                              </InputAdornment>
                            )}
                          </>
                        ),
                      }}
                    />
                  )}
                  getOptionDisabled={(option) => {
                    if (documentType === 5 && option.NotForMovements) return true;
                    if (documentType === 4 && option.NotForReturns) return true;
                    return false;
                  }}
                  renderOption={(props, option) => {
                    const isBlocked = (documentType === 5 && option.NotForMovements) || (documentType === 4 && option.NotForReturns);
                    return (
                      <Box component="li" {...props} sx={{ opacity: isBlocked ? 0.6 : 1 }}>
                        <Box sx={{ flex: 1 }}>
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.25 }}>
                            <Typography variant="body2" component="div" sx={{ fontWeight: 600 }}>
                              {option.ProductName}
                            </Typography>
                            {option.NotForMovements && documentType === 5 && (
                              <Chip
                                label="Запрет перемещения"
                                size="small"
                                color="error"
                                sx={{ height: 18, fontSize: '0.65rem', fontWeight: 700 }}
                              />
                            )}
                            {option.NotForReturns && documentType === 4 && (
                              <Chip
                                label="Запрет возврата"
                                size="small"
                                color="error"
                                sx={{ height: 18, fontSize: '0.65rem', fontWeight: 700 }}
                              />
                            )}
                          </Box>
                          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 0.5 }}>
                            <Typography variant="caption" color="text.secondary">
                              Код: {option.ProductSku} | ШК: {option.Barcode} | {option.Category || 'Без категории'} {option.Manager ? `| Менеджер: ${option.Manager}` : ''}
                            </Typography>
                            {(option.StockCount !== undefined || option.StockPrice !== undefined) && (
                              <Typography variant="caption" sx={{ ml: 1, whiteSpace: 'nowrap' }}>
                                {option.StockCount !== undefined && (
                                  <span style={{ color: '#2e7d32', fontWeight: 600 }}>
                                    {option.StockCount} уп
                                  </span>
                                )}
                                {option.StockPrice !== undefined && option.StockPrice > 0 && (
                                  <span style={{ color: '#666', marginLeft: 8 }}>
                                    {option.StockPrice.toLocaleString('ru-RU')} ₸
                                  </span>
                                )}
                              </Typography>
                            )}
                          </Box>
                        </Box>
                      </Box>
                    );
                  }}
                  noOptionsText={
                    localInputValue && !searchTriggered
                      ? 'Нажмите Enter для поиска'
                      : 'Товары не найдены'
                  }
                  loadingText="Поиск..."
                />
              </Grid>

              {/* Скрытые поля (заполняются автоматически) */}
              <Grid item xs={12} sm={3}>
                <TextField
                  fullWidth
                  size="small"
                  label="Код товара"
                  value={row.MaterialCode}
                  disabled
                  InputProps={{
                    style: { backgroundColor: '#f5f5f5' },
                    endAdornment: row.MaterialCode && (
                      <InputAdornment position="end">
                        <CheckIcon color="success" fontSize="small" />
                      </InputAdornment>
                    )
                  }}
                />
              </Grid>
              <Grid item xs={12} sm={3}>
                <TextField
                  fullWidth
                  size="small"
                  label="Штрихкод"
                  value={row.Barcode}
                  onChange={(e) => onUpdateTableRow(row.id, 'Barcode', e.target.value)}
                  required
                  error={!row.Barcode}
                  helperText={!row.Barcode ? 'Обязательное поле' : ''}
                  disabled={isDisabled}
                  InputProps={{
                    endAdornment: row.Barcode && (
                      <InputAdornment position="end">
                        <CheckIcon color="success" fontSize="small" />
                      </InputAdornment>
                    )
                  }}
                />
              </Grid>

              {/* Редактируемые поля - Количество */}
              {row.selectedMaterial?.PartExists && (row.selectedMaterial?.PartCoeff ?? 0) > 0 ? (
                <>
                  <Grid item xs={6} sm={3}>
                    {(() => {
                      const stockMax = row.selectedMaterial?.StockCount;
                      const qty = parseFloat(row.Quantity);
                      const overStock = stockMax !== undefined && qty > stockMax;
                      const baseError = !(row.Quantity && qty > 0) && !(row.QuantityPcs && parseFloat(row.QuantityPcs) > 0);
                      return (
                        <TextField
                          fullWidth
                          size="small"
                          type="number"
                          label="Упаковки"
                          value={row.Quantity}
                          onChange={(e) => {
                            const value = e.target.value.replace(/[^0-9]/g, '');
                            onUpdateTableRow(row.id, 'Quantity', value);
                          }}
                          error={baseError || overStock}
                          helperText={overStock ? `Остаток: ${stockMax} упак` : ''}
                          placeholder="0"
                          inputProps={{ min: 0, step: 1, max: stockMax }}
                          disabled={isDisabled}
                          InputProps={{
                            endAdornment: row.Quantity && qty > 0 && !overStock && (
                              <InputAdornment position="end">
                                <Typography variant="caption" color="text.secondary">упак</Typography>
                              </InputAdornment>
                            )
                          }}
                        />
                      );
                    })()}
                  </Grid>
                  <Grid item xs={6} sm={3}>
                    <TextField
                      fullWidth
                      size="small"
                      type="number"
                      label="Штуки"
                      value={row.QuantityPcs || ''}
                      onChange={(e) => {
                        const value = e.target.value.replace(/[^0-9]/g, '');
                        onUpdateTableRow(row.id, 'QuantityPcs', value);
                      }}
                      placeholder="0"
                      inputProps={{ min: 0, step: 1 }}
                      disabled={isDisabled}
                      InputProps={{
                        endAdornment: row.QuantityPcs && parseFloat(row.QuantityPcs) > 0 && (
                          <InputAdornment position="end">
                            <Typography variant="caption" color="text.secondary">шт</Typography>
                          </InputAdornment>
                        )
                      }}
                    />
                  </Grid>
                </>
              ) : (
                <Grid item xs={12} sm={6}>
                  {(() => {
                    const stockMax = row.selectedMaterial?.StockCount;
                    const qty = parseFloat(row.Quantity);
                    const overStock = stockMax !== undefined && qty > stockMax;
                    return (
                      <TextField
                        fullWidth
                        size="small"
                        type="number"
                        label="Количество (упак)"
                        value={row.Quantity}
                        onChange={(e) => {
                          const value = e.target.value.replace(/[^0-9]/g, '');
                          onUpdateTableRow(row.id, 'Quantity', value);
                        }}
                        required
                        error={!row.Quantity || qty <= 0 || overStock}
                        helperText={overStock ? `Остаток: ${stockMax} упак` : ''}
                        placeholder="0"
                        inputProps={{ min: 1, step: 1, max: stockMax }}
                        disabled={isDisabled}
                        InputProps={{
                          endAdornment: row.Quantity && !overStock && (
                            <InputAdornment position="end">
                              <CheckIcon color="success" fontSize="small" />
                            </InputAdornment>
                          )
                        }}
                      />
                    );
                  })()}
                </Grid>
              )}

              {/* Маркировочные коды */}
              <Grid item xs={12}>
                <MarkCodesInput
                  row={row}
                  onUpdateTableRow={onUpdateTableRow}
                  isDisabled={isDisabled}
                  duplicateCodes={duplicateCodes}
                  hidePartialScanning={documentType === 4 && isMarkedProduct}
                />
              </Grid>

              {/* Серии товара */}
              {documentType === 4 && (
                <Grid item xs={12}>
                  <SeriesSection
                    row={row}
                    onUpdateTableRow={onUpdateTableRow}
                  />
                </Grid>
              )}
            </Grid>
          </Box>
        )
      }
    </Paper >
  );
};

// ============ КОМПОНЕНТ СЕРИЙ ТОВАРА (группировка: Серия + Кол-во) ============
interface SeriesSectionProps {
  row: TableRowData;
  onUpdateTableRow: (id: string, field: keyof TableRowData, value: string) => void;
}

interface GroupedSeriesRow {
  id: string;
  name: string;
  guid: string;
  count: number | string;
}

const API_BASE = 'https://tbl.zerdegroup.kz/api/v1';

// Helper: парсинг row.series в сгруппированные строки
const parseToGroupedSeries = (seriesJson?: string, defaultQty: number = 0): GroupedSeriesRow[] => {
  if (!seriesJson || seriesJson === '[]') {
    return [{
      id: Math.random().toString(36).substring(2, 9),
      name: '',
      guid: '00000000-0000-0000-0000-000000000000',
      count: Math.max(1, defaultQty)
    }];
  }

  try {
    const parsed = JSON.parse(seriesJson);
    if (Array.isArray(parsed) && parsed.length > 0) {
      const groupMap = new Map<string, GroupedSeriesRow>();
      for (const item of parsed) {
        const name = (typeof item === 'string' ? item : item?.name || '').trim();
        const guid = (typeof item === 'string' ? '00000000-0000-0000-0000-000000000000' : item?.guid || '00000000-0000-0000-0000-000000000000');
        const key = `${name.toLowerCase()}:::${guid.toLowerCase()}`;
        const existing = groupMap.get(key);
        if (existing) {
          existing.count = (Number(existing.count) || 0) + 1;
        } else {
          groupMap.set(key, {
            id: Math.random().toString(36).substring(2, 9),
            name,
            guid,
            count: 1
          });
        }
      }
      const result = Array.from(groupMap.values());
      if (result.length > 0) {
        return result;
      }
    }
  } catch { }

  return [{
    id: Math.random().toString(36).substring(2, 9),
    name: '',
    guid: '00000000-0000-0000-0000-000000000000',
    count: Math.max(1, defaultQty)
  }];
};

// Helper: разворачивание GroupedSeriesRow[] в плоский массив для сохранения
const serializeGroupedSeries = (groups: GroupedSeriesRow[]): string => {
  const flatArray: Array<{ name: string; guid: string }> = [];
  for (const g of groups) {
    const name = g.name.trim();
    const guid = g.guid || '00000000-0000-0000-0000-000000000000';
    const count = Math.max(0, Math.floor(Number(g.count) || 0));
    for (let i = 0; i < count; i++) {
      flatArray.push({ name, guid });
    }
  }
  return JSON.stringify(flatArray);
};

const SeriesSection: React.FC<SeriesSectionProps> = ({ row, onUpdateTableRow }) => {
  const [availableSeries, setAvailableSeries] = useState<any[]>([]);
  const [seriesLoading, setSeriesLoading] = useState(false);

  const qty = Math.ceil(parseFloat(row.Quantity || '0') || 0);

  const [groupedRows, setGroupedRows] = useState<GroupedSeriesRow[]>(() =>
    parseToGroupedSeries(row.series, qty)
  );

  const lastSerializedRef = useRef<string>(row.series || '');
  const prevQtyRef = useRef<number>(qty);
  const rowIdRef = useRef<string>(row.id);
  rowIdRef.current = row.id;

  // Загружаем доступные серии из 1С при смене товара
  useEffect(() => {
    const productZid = row.selectedMaterial?.ProductZid;
    if (!productZid) { setAvailableSeries([]); return; }
    let cancelled = false;
    setSeriesLoading(true);
    axios.get(`${API_BASE}/journal/series/${productZid}`)
      .then(res => {
        if (cancelled) return;
        if (res.data?.success) {
          setAvailableSeries(res.data.data || []);
        } else {
          setAvailableSeries([]);
        }
      })
      .catch(() => { if (!cancelled) setAvailableSeries([]); })
      .finally(() => { if (!cancelled) setSeriesLoading(false); });
    return () => { cancelled = true; };
  }, [row.selectedMaterial?.ProductZid]);

  // Синхронизация при внешнем изменении row.series
  useEffect(() => {
    if (row.series !== lastSerializedRef.current) {
      lastSerializedRef.current = row.series || '';
      setGroupedRows(parseToGroupedSeries(row.series, qty));
    }
  }, [row.series, qty]);

  // Первичная инициализация row.series, если он пуст, но qty > 0
  useEffect(() => {
    if (!row.series && qty > 0) {
      const initial = parseToGroupedSeries(undefined, qty);
      const serialized = serializeGroupedSeries(initial);
      lastSerializedRef.current = serialized;
      onUpdateTableRow(row.id, 'series', serialized);
      setGroupedRows(initial);
    }
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  // Обновление количества при смене Quantity товара:
  // Если у нас ровно 1 серия, обновляем ее количество под новый qty
  useEffect(() => {
    if (prevQtyRef.current !== qty && qty > 0) {
      prevQtyRef.current = qty;
      setGroupedRows(prev => {
        if (prev.length === 1) {
          const updated = [{ ...prev[0], count: qty }];
          const serialized = serializeGroupedSeries(updated);
          lastSerializedRef.current = serialized;
          onUpdateTableRow(rowIdRef.current, 'series', serialized);
          return updated;
        }
        return prev;
      });
    } else {
      prevQtyRef.current = qty;
    }
  }, [qty, onUpdateTableRow]);

  const syncToParent = useCallback((rows: GroupedSeriesRow[]) => {
    const serialized = serializeGroupedSeries(rows);
    lastSerializedRef.current = serialized;
    onUpdateTableRow(rowIdRef.current, 'series', serialized);
  }, [onUpdateTableRow]);

  const handleSelectChange = (id: string, val: any) => {
    setGroupedRows(prev => {
      const updated = prev.map(item => {
        if (item.id !== id) return item;
        if (!val) {
          return { ...item, name: '', guid: '00000000-0000-0000-0000-000000000000' };
        }
        if (typeof val === 'string') {
          const matched = availableSeries.find(s => s.SeriesName.toLowerCase() === val.toLowerCase());
          return {
            ...item,
            name: matched ? matched.SeriesName : val,
            guid: matched ? matched.SeriesZid : '00000000-0000-0000-0000-000000000000'
          };
        }
        return {
          ...item,
          name: val.SeriesName || '',
          guid: val.SeriesZid || '00000000-0000-0000-0000-000000000000'
        };
      });
      syncToParent(updated);
      return updated;
    });
  };

  const handleInputChange = (id: string, val: string, reason: string) => {
    if (reason === 'input') {
      setGroupedRows(prev => {
        const matched = availableSeries.find(s => s.SeriesName.toLowerCase() === val.toLowerCase());
        const updated = prev.map(item =>
          item.id === id
            ? { ...item, name: val, guid: matched ? matched.SeriesZid : '00000000-0000-0000-0000-000000000000' }
            : item
        );
        syncToParent(updated);
        return updated;
      });
    }
  };

  const handleCountChange = (id: string, newCountStr: string) => {
    // Оставляем только цифры
    const cleanStr = newCountStr.replace(/[^0-9]/g, '');
    setGroupedRows(prev => {
      const updated = prev.map(item => item.id === id ? { ...item, count: cleanStr } : item);
      syncToParent(updated);
      return updated;
    });
  };

  const handleCountBlur = (id: string) => {
    setGroupedRows(prev => {
      const updated = prev.map(item => {
        if (item.id === id) {
          const num = Math.max(1, Math.floor(Number(item.count) || 0));
          return { ...item, count: num };
        }
        return item;
      });
      syncToParent(updated);
      return updated;
    });
  };

  const handleAddSeriesRow = () => {
    const currentSum = groupedRows.reduce((sum, item) => sum + (Number(item.count) || 0), 0);
    const remaining = Math.max(1, qty - currentSum);
    setGroupedRows(prev => {
      const updated = [
        ...prev,
        {
          id: Math.random().toString(36).substring(2, 9),
          name: '',
          guid: '00000000-0000-0000-0000-000000000000',
          count: remaining
        }
      ];
      syncToParent(updated);
      return updated;
    });
  };

  const handleDeleteSeriesRow = (id: string) => {
    if (groupedRows.length <= 1) return;
    setGroupedRows(prev => {
      const updated = prev.filter(item => item.id !== id);
      syncToParent(updated);
      return updated;
    });
  };

  const getOptionLabel = useCallback((opt: any) => {
    return (typeof opt === 'string' ? opt : opt.SeriesName) || '';
  }, []);

  const filterOptions = useCallback((options: any[], state: any) => {
    const inputValue = state.inputValue.toLowerCase();
    const filtered = options.filter(option => {
      const name = (typeof option === 'string' ? option : option.SeriesName) || '';
      return name.toLowerCase().includes(inputValue);
    });
    return filtered.slice(0, 100);
  }, []);

  const isOptionEqualToValue = useCallback((option: any, value: any) => {
    const optName = typeof option === 'string' ? option : option.SeriesName;
    const valName = typeof value === 'string' ? value : value.name;
    return optName === valName;
  }, []);

  if (qty <= 0) return null;

  const currentSum = groupedRows.reduce((sum, item) => sum + (Number(item.count) || 0), 0);
  const allRowsHaveName = groupedRows.every(item => item.name && item.name.trim().length > 0);
  const isExactSum = currentSum === qty;
  const isOverfilled = currentSum > qty;
  const isUnderfilled = currentSum < qty;
  const isValid = isExactSum && allRowsHaveName;

  return (
    <Box sx={{ border: '1px solid #e0e0e0', borderRadius: 1.5, p: 2, bgcolor: '#fafafa', mt: 1 }}>
      {/* Заголовок */}
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1.5, flexWrap: 'wrap', gap: 1 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <Typography variant="body2" fontWeight="bold">Серии товара</Typography>
          <Chip
            icon={isValid ? <CheckIcon fontSize="small" /> : (isUnderfilled || isOverfilled ? <WarningIcon fontSize="small" /> : undefined)}
            label={
              seriesLoading
                ? 'Загрузка серий...'
                : isValid
                  ? `Все серии распределены (${currentSum}/${qty} уп)`
                  : isOverfilled
                    ? `Превышение: ${currentSum}/${qty} уп (+${currentSum - qty})`
                    : isUnderfilled
                      ? `Не распределено: ${qty - currentSum} из ${qty} уп`
                      : `Укажите серии (${groupedRows.filter(r => r.name.trim()).length}/${groupedRows.length})`
            }
            size="small"
            color={isValid ? 'success' : (isOverfilled ? 'error' : 'warning')}
            variant={isValid ? 'filled' : 'outlined'}
            sx={{ fontWeight: 500 }}
          />
          {seriesLoading && <CircularProgress size={14} sx={{ ml: 0.5 }} />}
        </Box>
        {availableSeries.length > 0 && (
          <Typography variant="caption" color="text.secondary">
            Доступно в 1С: {availableSeries.length} серий
          </Typography>
        )}
      </Box>

      {/* Список сгруппированных серий */}
      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
        {groupedRows.map((seriesRow, index) => {
          const isEmptyName = !seriesRow.name || !seriesRow.name.trim();
          const rowCountNum = Number(seriesRow.count) || 0;
          const isCountInvalid = rowCountNum <= 0;

          const selectedOption = seriesRow.name
            ? (availableSeries.find(s => s.SeriesName === seriesRow.name) || seriesRow.name)
            : null;

          return (
            <Paper
              key={seriesRow.id}
              elevation={0}
              sx={{
                p: 1.5,
                border: '1px solid',
                borderColor: isEmptyName ? '#fed7aa' : '#e2e8f0',
                borderRadius: 1.5,
                bgcolor: isEmptyName ? '#fffdf7' : '#ffffff'
              }}
            >
              <Grid container spacing={1.5} alignItems="center">
                {/* Выбор серии */}
                <Grid item xs={12} sm={7} md={7}>
                  <Autocomplete
                    freeSolo
                    size="small"
                    options={availableSeries}
                    getOptionLabel={getOptionLabel}
                    filterOptions={filterOptions}
                    isOptionEqualToValue={isOptionEqualToValue}
                    value={selectedOption}
                    onChange={(_, val) => handleSelectChange(seriesRow.id, val)}
                    onInputChange={(_, val, reason) => handleInputChange(seriesRow.id, val, reason)}
                    renderOption={(props, option) => {
                      const { key, ...optionProps } = props as any;
                      const name = typeof option === 'string' ? option : option.SeriesName;
                      return (
                        <Box component="li" key={key || name} {...optionProps} sx={{ fontSize: '0.85rem' }}>
                          {name}
                        </Box>
                      );
                    }}
                    renderInput={(params) => (
                      <TextField
                        {...params}
                        label={groupedRows.length > 1 ? `Серия ${index + 1}` : 'Серия'}
                        error={isEmptyName}
                        placeholder="Введите или выберите серию из 1С"
                        helperText={isEmptyName ? 'Выберите из списка или введите вручную' : undefined}
                        InputProps={{
                          ...params.InputProps,
                          style: {
                            backgroundColor: isEmptyName ? '#fffbeb' : '#f0fdf4',
                            fontSize: '0.85rem'
                          }
                        }}
                      />
                    )}
                  />
                </Grid>

                {/* Количество товара с этой серией */}
                <Grid item xs={8} sm={3.5} md={3.5}>
                  <TextField
                    size="small"
                    fullWidth
                    label="Кол-во с этой серией"
                    type="text"
                    value={seriesRow.count}
                    error={isCountInvalid}
                    onChange={(e) => handleCountChange(seriesRow.id, e.target.value)}
                    onBlur={() => handleCountBlur(seriesRow.id)}
                    helperText={isCountInvalid ? 'Минимум 1' : undefined}
                    InputProps={{
                      endAdornment: <InputAdornment position="end">уп</InputAdornment>,
                      style: { fontSize: '0.85rem' }
                    }}
                  />
                </Grid>

                {/* Кнопка удаления */}
                <Grid item xs={4} sm={1.5} md={1.5} sx={{ textAlign: 'right' }}>
                  <Tooltip title={groupedRows.length <= 1 ? 'Нельзя удалить единственную серию' : 'Удалить эту серию'}>
                    <span>
                      <IconButton
                        size="small"
                        color="error"
                        disabled={groupedRows.length <= 1}
                        onClick={() => handleDeleteSeriesRow(seriesRow.id)}
                        sx={{
                          border: '1px solid',
                          borderColor: groupedRows.length <= 1 ? 'transparent' : '#fecaca',
                          '&:hover': { bgcolor: '#fee2e2' }
                        }}
                      >
                        <DeleteIcon fontSize="small" />
                      </IconButton>
                    </span>
                  </Tooltip>
                </Grid>
              </Grid>
            </Paper>
          );
        })}
      </Box>

      {/* Нижняя панель действий */}
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mt: 1.5, flexWrap: 'wrap', gap: 1 }}>
        <Button
          size="small"
          variant="outlined"
          startIcon={<AddIcon />}
          onClick={handleAddSeriesRow}
          disabled={isOverfilled || currentSum >= qty}
          sx={{
            textTransform: 'none',
            fontSize: '0.82rem',
            borderColor: '#cbd5e1',
            color: '#334155',
            '&:hover': { borderColor: '#94a3b8', bgcolor: '#f1f5f9' }
          }}
        >
          Добавить серию
        </Button>

        <Typography variant="caption" color={isExactSum ? 'text.secondary' : (isOverfilled ? 'error' : 'warning.main')} sx={{ fontWeight: 500 }}>
          {isUnderfilled && `Осталось распределить: ${qty - currentSum} уп из ${qty}`}
          {isOverfilled && `Внимание: сумма серий превышает количество товара на ${currentSum - qty} уп`}
          {isExactSum && `Всего товара: ${qty} уп`}
        </Typography>
      </Box>
    </Box>
  );
};

export default DocumentDialog;